/**
 * WaitlistPanel - Vendor Orders Style Theme
 *
 * Clean white cards, amber/orange accents, consistent styling
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Notifications as NotifyIcon,
  EventSeat as SeatIcon,
  Close as RemoveIcon,
  AccessTime as TimeIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useReservations } from '../../context/ReservationContext';
import { useConfirm } from '../../context/ConfirmContext';
import { WaitlistBadge } from './ReservationBadge';
import type { WaitlistEntry, WaitlistRequest } from '../../types/reservation.types';
import toast from 'react-hot-toast';

// ============================================================================
// STYLES
// ============================================================================

const styles = `
  .waitlist-panel {
    background: white;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .waitlist-header {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #E2E8F0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .add-button {
    background: var(--action);
    color: var(--action-text);
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.8125rem;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .add-button:hover {
    background: var(--action-hover);
    transform: translateY(-1px);
  }

  .stats-row {
    padding: 0.75rem 1.25rem;
    background: #F8FAFC;
    display: flex;
    gap: 0.75rem;
    border-bottom: 1px solid #E2E8F0;
  }

  .stat-chip {
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .stat-chip.waiting {
    background: #FEF3C7;
    color: var(--action-hover);
    border: 1px solid #FCD34D;
  }

  .stat-chip.notified {
    background: #EDE9FE;
    color: #7C3AED;
    border: 1px solid #C4B5FD;
  }

  .waitlist-content {
    flex: 1;
    overflow-y: auto;
  }

  .waitlist-item {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #F1F5F9;
    transition: background 0.15s ease;
  }

  .waitlist-item:hover {
    background: #F8FAFC;
  }

  .waitlist-item:last-child {
    border-bottom: none;
  }

  .action-button {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }

  .action-button.notify {
    background: #EDE9FE;
    color: #7C3AED;
  }

  .action-button.notify:hover {
    background: #DDD6FE;
  }

  .action-button.seat {
    background: #D1FAE5;
    color: #059669;
  }

  .action-button.seat:hover {
    background: #A7F3D0;
  }

  .action-button.remove {
    background: #FEE2E2;
    color: #DC2626;
  }

  .action-button.remove:hover {
    background: #FECACA;
  }

  .party-size-control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .party-btn {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    background: white;
    font-size: 1.25rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }

  .party-btn:hover {
    background: #F8FAFC;
    border-color: #CBD5E1;
  }
`;

interface WaitlistPanelProps {
  onSelectTable?: (entry: WaitlistEntry, action: 'notify' | 'seat') => void;
  className?: string;
}

const WaitlistPanel: React.FC<WaitlistPanelProps> = ({
  onSelectTable,
  className = '',
}) => {
  const navigate = useNavigate();
  const {
    waitlist,
    waitlistStats,
    addToWaitlist,
    seatWaitlistCustomer,
    removeFromWaitlist,
    isWaitlistEnabled,
  } = useReservations();
  const confirm = useConfirm();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [loading, setLoading] = useState<number | null>(null);
  const [newEntry, setNewEntry] = useState<WaitlistRequest>({
    customerName: '',
    customerPhone: '',
    partySize: 2,
  });

  if (!isWaitlistEnabled) {
    return null;
  }

  const activeWaitlist = waitlist.filter(
    (w) => w.status === 'WAITING' || w.status === 'NOTIFIED'
  );

  const handleAddToWaitlist = async () => {
    if (!newEntry.customerName || !newEntry.customerPhone) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(-1);
      await addToWaitlist(newEntry);
      toast.success('Added to waitlist');
      setAddDialogOpen(false);
      setNewEntry({ customerName: '', customerPhone: '', partySize: 2 });
    } catch (err) {
      toast.error('Failed to add to waitlist');
    } finally {
      setLoading(null);
    }
  };

  const handleNotify = (entry: WaitlistEntry) => {
    // Reuse the live floor plan with a banner instead of a separate picker.
    navigate(`/vendor/tables?waitlistSeat=${entry.id}&action=notify`);
  };

  const handleSeat = async (entry: WaitlistEntry) => {
    // If already notified with a table, can seat directly without picking again.
    if (entry.status === 'NOTIFIED' && (entry.assignedTableId || entry.assignedTableGroupId)) {
      try {
        setLoading(entry.id);
        await seatWaitlistCustomer(entry.id);
        toast.success(`${entry.customerName} has been seated`);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to seat customer';
        toast.error(message);
      } finally {
        setLoading(null);
      }
    } else {
      navigate(`/vendor/tables?waitlistSeat=${entry.id}&action=seat`);
    }
  };

  const handleRemove = async (entry: WaitlistEntry) => {
    if (!(await confirm({
      title: 'Remove from waitlist?',
      description: `${entry.customerName} · party of ${entry.partySize}. They'll drop off the queue.`,
      confirmText: 'Remove',
      cancelText: 'Keep',
    }))) return;
    try {
      setLoading(entry.id);
      await removeFromWaitlist(entry.id);
      toast.success('Removed from waitlist');
    } catch (err) {
      toast.error('Failed to remove from waitlist');
    } finally {
      setLoading(null);
    }
  };

  const formatWaitTime = (minutes: number | undefined) => {
    if (!minutes) return '--';
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <>
      <style>{styles}</style>
      <div className={`waitlist-panel ${className}`}>
        {/* Header */}
        <div className="waitlist-header">
          <div>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              Waitlist
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
              {waitlistStats?.totalActive || 0} waiting
            </Typography>
          </div>
          <button className="add-button" onClick={() => setAddDialogOpen(true)}>
            <AddIcon sx={{ fontSize: 18 }} />
            Add
          </button>
        </div>

        {/* Stats */}
        {waitlistStats && waitlistStats.totalActive > 0 && (
          <div className="stats-row">
            <span className="stat-chip waiting">
              {waitlistStats.waiting} waiting
            </span>
            <span className="stat-chip notified">
              {waitlistStats.notified} notified
            </span>
          </div>
        )}

        {/* Waitlist Items */}
        <div className="waitlist-content">
          {activeWaitlist.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} /> {/* basil-todo */}
              <Typography variant="body2" sx={{ color: 'var(--text-tertiary)' }}>
                No customers waiting
              </Typography>
            </div>
          ) : (
            activeWaitlist.map((entry) => (
              <div key={entry.id} className="waitlist-item">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {entry.queuePosition}. {entry.customerName}
                      </Typography>
                      <WaitlistBadge status={entry.status} size="small" />
                    </div>
                    <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      <span className="flex items-center gap-1">
                        <PeopleIcon sx={{ fontSize: 16 }} />
                        {entry.partySize}
                      </span>
                      <span className="flex items-center gap-1">
                        <PhoneIcon sx={{ fontSize: 16 }} />
                        {entry.customerPhone}
                      </span>
                      {entry.estimatedWaitMinutes && (
                        <span className="flex items-center gap-1">
                          <TimeIcon sx={{ fontSize: 16 }} />
                          ~{formatWaitTime(entry.estimatedWaitMinutes)}
                        </span>
                      )}
                    </div>
                    {entry.specialRequests && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '0.8125rem' }}
                      >
                        "{entry.specialRequests}"
                      </Typography>
                    )}
                  </div>
                  <div className="flex gap-1 ml-2">
                    {entry.status === 'WAITING' && (
                      <Tooltip title="Notify customer">
                        <button
                          className="action-button notify"
                          onClick={() => handleNotify(entry)}
                          disabled={loading === entry.id}
                        >
                          {loading === entry.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <NotifyIcon sx={{ fontSize: 18 }} />
                          )}
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Seat customer">
                      <button
                        className="action-button seat"
                        onClick={() => handleSeat(entry)}
                        disabled={loading === entry.id}
                      >
                        <SeatIcon sx={{ fontSize: 18 }} />
                      </button>
                    </Tooltip>
                    <Tooltip title="Remove from waitlist">
                      <button
                        className="action-button remove"
                        onClick={() => handleRemove(entry)}
                        disabled={loading === entry.id}
                      >
                        <RemoveIcon sx={{ fontSize: 18 }} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add to Waitlist Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid var(--border-subtle)', pb: 2, fontWeight: 600 }}>
          Add to Waitlist
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <div className="space-y-4">
            <TextField
              label="Customer Name"
              fullWidth
              value={newEntry.customerName}
              onChange={(e) =>
                setNewEntry((prev) => ({ ...prev, customerName: e.target.value }))
              }
              required
            />
            <TextField
              label="Phone Number"
              fullWidth
              value={newEntry.customerPhone}
              onChange={(e) =>
                setNewEntry((prev) => ({ ...prev, customerPhone: e.target.value }))
              }
              required
            />
            <div>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'var(--text-secondary)' }}>
                Party Size
              </Typography>
              <div className="party-size-control">
                <button
                  className="party-btn"
                  onClick={() =>
                    setNewEntry((prev) => ({
                      ...prev,
                      partySize: Math.max(1, prev.partySize - 1),
                    }))
                  }
                >
                  -
                </button>
                <Typography
                  variant="h6"
                  sx={{ width: 48, textAlign: 'center', fontWeight: 600, color: 'var(--text-primary)' }}
                >
                  {newEntry.partySize}
                </Typography>
                <button
                  className="party-btn"
                  onClick={() =>
                    setNewEntry((prev) => ({
                      ...prev,
                      partySize: prev.partySize + 1,
                    }))
                  }
                >
                  +
                </button>
              </div>
            </div>
            <TextField
              label="Special Requests (optional)"
              fullWidth
              multiline
              rows={2}
              value={newEntry.specialRequests || ''}
              onChange={(e) =>
                setNewEntry((prev) => ({
                  ...prev,
                  specialRequests: e.target.value,
                }))
              }
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid var(--border-subtle)' }}>
          <Button onClick={() => setAddDialogOpen(false)} sx={{ color: 'var(--text-tertiary)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToWaitlist}
            disabled={loading === -1}
            sx={{
              background: 'var(--action)',
              '&:hover': { background: 'var(--action-hover)' },
            }}
          >
            {loading === -1 ? <CircularProgress size={20} color="inherit" /> : 'Add to Waitlist'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WaitlistPanel;
